import { Checkbox, Switch } from "@progress/kendo-react-inputs";
import useAliceGPT from "../../../../Hooks/AliceGPT/useAliceGPT";
import { DISPLAY_CHECKBOX_TOGGLED, TOGGLE_DISPLAY_CONFIGURATION_LOADED, TOGGLE_LOAD_PORTALS } from "../../../../Context/AliceGPT/AliceGPTContextEvents";
import { useTranslation } from "react-i18next";
import { getEntityMenuAndPageConfiguration } from "../../../../API/AliceGPT/AliceGPT";
import { useEffect, useState } from "react";
import { getDatalakeImageUrl } from "../../../Helpers/DateLakeStorage";
import { Tooltip } from '@progress/kendo-react-tooltip';
import PortalDialog from './PortalDialog';

function EditPageConfiguration({ useAI, maxPortals }) {

    const { i18n } = useTranslation();
    const { t } = useTranslation("create_project/edit_page_configurations");

    const [loading, setLoading] = useState(true);
    const [state, dispatch] = useAliceGPT();
    const portals = state.portals || [];
    const [showPortalDialog, setShowPortalDialog] = useState(false);
    const [portalDialogMode, setPortalDialogMode] = useState('create'); // 'create' or 'edit'
    const [portalDialogData, setPortalDialogData] = useState({ portalName: '', description: '' });
    const [editingPortalIdx, setEditingPortalIdx] = useState(null);

    const selectedEntities = state.entities.filter(entity => entity.checked);

    const enabledColumns = [
        {
            title: t("table"),
            databaseField: "shouldCreateTable",
            disabledField: "isTableDisabled"
        },
        {
            title: t("split_view"),
            databaseField: "shouldCreateSplitView",
            disabledField: ""
        },
        {
            title: t("kanban"),
            databaseField: "shouldCreateKanban",
            disabledField: "isKanbanDisabled"
        },
        {
            title: t("calendar"),
            databaseField: "shouldCreateCalendar",
            disabledField: "isCalendarDisabled"
        },
        {
            title: t("cards"),
            databaseField: "shouldCreateCards",
            disabledField: ""
        },
        {
            title: t("gantt_chart"),
            databaseField: "shouldCreateGanttChart",
            disabledField: "isGanttChartDisabled"
        }
    ];

    useEffect(() => {
        debugger;
        if (state.shouldLoadPortals) {
            setLoading(true);
            optimizeUsingAI()
                .finally(() => {
                    setLoading(false);
                    dispatch({
                        type: TOGGLE_DISPLAY_CONFIGURATION_LOADED
                    })
                    dispatch({
                        type: TOGGLE_LOAD_PORTALS
                    })
                })
        } else {
            if (state.isPageConfigurationLoading) {
                dispatch({
                    type: TOGGLE_DISPLAY_CONFIGURATION_LOADED
                })
            }
            setLoading(false)
        }
    }, []);

    function handleCheckbox(event, entityName) {
        const { name } = event.target;
        dispatch({
            type: DISPLAY_CHECKBOX_TOGGLED,
            payload: {
                entityName,
                name
            }
        })
    }

    async function optimizeUsingAI() {
        try {
            const entities = selectedEntities
                .map(entity => ({
                    name: entity.name,
                    attributes: entity.attributes.filter(attribute => attribute.selected).map(attribute => ({
                        name: attribute.name,
                        type: attribute.type
                    }))
                }))
            const response = await getEntityMenuAndPageConfiguration(entities, maxPortals, state.form.language || i18n.language);
            if (response.portals) {
                // Transform portals to include checked state for each entity
                const transformedPortals = response.portals.map((portal, index) => {
                    // If backend returns checked entities, use them; else fallback to all unchecked
                    const checkedEntities = portal.entities || entities.map(entity => entity.name);
                    const shouldCheckEntities = index + 1 <= maxPortals;
                    return {
                        ...portal, // First portal is the default portal
                        hasMobilePortal: false,
                        entities: entities.map(entity => ({
                            name: entity.name,
                            checked: entity.name === "User Profile" ? true : (shouldCheckEntities ? checkedEntities.includes(entity.name) : false)
                        }))
                    };
                });
                dispatch({ type: 'PORTALS_UPDATED', payload: transformedPortals });
            }
            // for (const element of response.configurations) {
            //     const currentEntityPageConfiguration = selectedEntities.find(entity => entity.name === element.entityName).pageConfiguration;
            //     const viewsToEnable = element.suitableViews.map(view => `shouldCreate${view.replace(/ /g, "")}`);
            //     for (const view of enabledColumns) {
            //         if (
            //             (viewsToEnable.includes(view.databaseField) && !currentEntityPageConfiguration[view.databaseField]) ||
            //             (!viewsToEnable.includes(view.databaseField) && currentEntityPageConfiguration[view.databaseField])
            //         ) {
            //             dispatch({
            //                 type: DISPLAY_CHECKBOX_TOGGLED,
            //                 payload: {
            //                     entityName: element.entityName,
            //                     name: view.databaseField
            //                 }
            //             })
            //         }
            //     }
            // }
        } catch (error) {
            throw new Error("Something went wrong!");
        }
    }

    function handlePortalEntityCheckbox(portalName, entityName) {
        const portal = portals.find(p => p.portalName === portalName);
        
        // Prevent unchecking entities if portal is default portal
        if (portal && portal.isDefaultPortal) {
            return;
        }
        
        // Prevent unchecking User Profile entity in any portal
        if (entityName === "User Profile") {
            return;
        }
        
        dispatch({
            type: 'PORTALS_UPDATED',
            payload: portals.map(portal => {
                if (portal.portalName !== portalName) return portal;
                const entity = portal.entities.find(e => e.name === entityName);
                const newEntityCheckedState = !entity.checked;
                const newPortal = {
                    ...portal,
                    entities: portal.entities.map(e => e.name === entityName ? { ...e, checked: newEntityCheckedState } : e)
                }
                const numEntitiesChecked = newPortal.entities.filter(e => e.checked).length;
                if (numEntitiesChecked === 1) {
                    const userProfileEntityIndex = newPortal.entities.findIndex(e => e.name === "User Profile");
                    const userProfileEntity = newPortal.entities[userProfileEntityIndex];
                    if (userProfileEntity.checked) {
                        newPortal.entities[userProfileEntityIndex].checked = false;
                    } else {
                        newPortal.entities[userProfileEntityIndex].checked = true;
                    }
                }
                return newPortal;
            })
        });
    }

    function openCreatePortalDialog() {
        setPortalDialogMode('create');
        setPortalDialogData({ portalName: '', description: '' });
        setEditingPortalIdx(null);
        setShowPortalDialog(true);
    }

    function openEditPortalDialog(idx) {
        setPortalDialogMode('edit');
        setPortalDialogData({
            portalName: portals[idx].portalName,
            description: portals[idx].description || ''
        });
        setEditingPortalIdx(idx);
        setShowPortalDialog(true);
    }

    function closePortalDialog() {
        setShowPortalDialog(false);
    }

    function handlePortalDialogChange(e) {
        const { name, value } = e.target;
        setPortalDialogData(prev => ({ ...prev, [name]: value }));
    }

    function handlePortalDialogSave() {
        let newPortals;
        if (portalDialogMode === 'create') {
            // Add all selectedEntities as checked when creating a new portal
            newPortals = [
                ...portals,
                {
                    portalName: portalDialogData.portalName,
                    description: portalDialogData.description,
                    isDefaultPortal: false, // New portals are not default
                    entities: selectedEntities.map(entity => ({
                        name: entity.name,
                        checked: entity.name === "User Profile" ? true : true
                    }))
                }
            ];
        } else if (portalDialogMode === 'edit' && editingPortalIdx !== null) {
            newPortals = portals.map((p, idx) => idx === editingPortalIdx ? {
                ...p,
                portalName: portalDialogData.portalName,
                description: portalDialogData.description,
                entities: p.entities.map(entity => ({
                    ...entity,
                    checked: entity.name === "User Profile" ? true : entity.checked
                }))
                // Preserve isDefaultPortal property
            } : p);
        } else {
            newPortals = portals;
        }
        dispatch({ type: 'PORTALS_UPDATED', payload: newPortals });
        setShowPortalDialog(false);
    }

    function handleSelectAllEntities(portalIdx) {
        const portal = portals[portalIdx];
        
        // Prevent unchecking all entities if portal is default portal
        if (portal && portal.isDefaultPortal) {
            return;
        }
        
        dispatch({
            type: 'PORTALS_UPDATED',
            payload: portals.map((portal, idx) => {
                if (idx !== portalIdx) return portal;
                const anyChecked = portal.entities.some(e => e.checked);
                const newCheckedState = !anyChecked;
                return {
                    ...portal,
                    entities: portal.entities.map(entity => ({
                        ...entity,
                        checked: entity.name === "User Profile" ? newCheckedState : newCheckedState
                    }))
                };
            })
        });
    }

    function isPortalSelected(portal) {
        return portal.entities.some(e => e.checked);
    }

    function handleMobilePortalChange(portalName) {
        dispatch({
            type: 'PORTALS_UPDATED',
            payload: portals.map(portal => portal.portalName === portalName ? { ...portal, hasMobilePortal: !portal.hasMobilePortal } : portal)
        });
    }

    if (loading) {
        return (
            <div className="h-100 w-100 d-flex flex-column justify-content-center align-items-center">
                <img src={getDatalakeImageUrl("loaders/RecommendationLoader.gif")} alt="" />
                <h3 className="mt-4">
                    {t("loading_message")}
                </h3>
            </div>
        )
    }

    return (
        <div className="h-100 p-3 bg-1">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <Tooltip position='right' anchorElement='target' tooltipStyle={{ maxWidth: "250px" }}>
                    <div className="d-flex align-items-center">
                        <h4 className="mb-0">{t('portals')}</h4>
                        &nbsp;
                        <i className="fa-solid fa-circle-info text-2 font-size-3" style={{ opacity: 0.5 }} title={'Choose portals based on team needs.'} />
                    </div>
                </Tooltip>
                <button className="btn btn-primary" onClick={openCreatePortalDialog}>
                    {t('new_portal')}
                </button>
            </div>
            <div className="row">
                {
                    portals.length === 0 && (
                        <div className="col-md-12">
                            <div className="card h-100 p-3 bg-white">
                                <div className="card-body">
                                    <h5 className="mb-0">{t('no_portals_message')}</h5>
                                </div>
                            </div>
                        </div>
                    )
                }
                {portals.map((portal, idx) => (
                    <div className="col-md-3 mb-4" key={portal.portalName}>
                        <div className={`card ${isPortalSelected(portal) ? "selected-portal-card" : ""} h-100 p-3 bg-white ${portal.isDefaultPortal ? "border-primary" : ""}`} style={{ borderRadius: 12 }}>
                            <div className="card-body">
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                    <div className="d-flex align-items-center">
                                        <span style={{ fontSize: 20, marginRight: 12 }}>
                                            {portal.fontAwesomeIcon ? (
                                                <i className={'fa-solid ' + portal.fontAwesomeIcon} style={{ color: idx === 0 ? '#3b82f6' : '#22c55e' }}></i>
                                            ) : (
                                                idx === 0 ? <i className="fa-solid fa-shield-halved" style={{ color: '#3b82f6' }}></i> : <i className="fa-solid fa-users" style={{ color: '#22c55e' }}></i>
                                            )}
                                        </span>
                                        <h5 className="mb-0" style={{ fontWeight: 700 }}>
                                            {portal.portalName}
                                            <span className="ms-2" style={{ cursor: 'pointer' }} onClick={() => openEditPortalDialog(idx)}><i className="fa-regular fa-pen fa-sm" style={{ opacity: 0.5, fontSize: 16 }}></i></span>
                                        </h5>
                                    </div>
                                    <Tooltip position='top' anchorElement='target' tooltipStyle={{ maxWidth: "250px" }}>
                                        <Checkbox
                                            className="ms-auto"
                                            checked={portal.entities.some(e => e.checked)}
                                            indeterminate={portal.entities.some(e => e.checked) && !portal.entities.every(e => e.checked)}
                                            onChange={() => handleSelectAllEntities(idx)}
                                            disabled={portal.isDefaultPortal}
                                            aria-label={`Select all entities in ${portal.portalName}`}
                                            title={portal.isDefaultPortal ? "Cannot uncheck all entities in default portal" : ""}
                                        />
                                    </Tooltip>
                                </div>
                                <div className="mb-2" style={{ color: '#64748b', fontSize: 14, fontWeight: 400 }}>
                                    {portal.description || (idx === 0 ? t('admin_portal_desc', 'Full system management') : t('customer_portal_desc', 'Customer-facing interface'))}
                                </div>
                                <div className="mb-2 d-flex align-items-center">
                                    <div>
                                        {t("mobile_portal_label")}
                                    </div>
                                    &nbsp;&nbsp;
                                    <Switch
                                        checked={portal.hasMobilePortal}
                                        onChange={() => handleMobilePortalChange(portal.portalName)}
                                    />
                                </div>
                                <div>
                                    {portal.entities.map((entity, i) => (
                                        <div className="d-flex align-items-center mb-2 justify-content-between" key={entity.name}>
                                            <span>{i + 1}. {entity.name}</span>
                                            <Tooltip position='top' anchorElement='target' tooltipStyle={{ maxWidth: "250px" }}>
                                                <Checkbox
                                                    checked={entity.checked}
                                                    onChange={() => handlePortalEntityCheckbox(portal.portalName, entity.name)}
                                                    disabled={(portal.isDefaultPortal && entity.checked) || entity.name === "User Profile"}
                                                    aria-label={`${portal.portalName}-${entity.name}`}
                                                    title={entity.name === "User Profile" ? "User Profile will be checked/unchecked with portal state" : (portal.isDefaultPortal && entity.checked ? "Cannot uncheck entities in default portal" : "")}
                                                />
                                            </Tooltip>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            {showPortalDialog && (
                <PortalDialog
                    show={showPortalDialog}
                    mode={portalDialogMode}
                    data={portalDialogData}
                    onChange={handlePortalDialogChange}
                    onClose={closePortalDialog}
                    onSave={handlePortalDialogSave}
                    portals={portals}
                    maxPortals={state.subscription.maxPortalsPerApp}
                    showMaxReachedError={portalDialogMode === 'create' && portals.length >= maxPortals}
                    editingPortalIdx={editingPortalIdx}
                />
            )}
        </div>
    )
}

export default EditPageConfiguration;